/**
 * How fresh the Brain index is, and the recovery action. Each document is
 * indexed when it is saved; a full re-index is for a changed embedding model, a
 * wiped index or an indexer outage. It runs page by page because one request
 * must stay under the backend's timeout.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Descriptions, Progress, Space, Typography, message } from 'antd';
import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { fetchBrainIndexStatus, reindexBrain } from '../../services/api.service';
import type { BrainIndexStatus } from '../../types/mcp';

const { Text } = Typography;

const BrainIndexCard: React.FC<{ tenant: string }> = ({ tenant }) => {
  const [status, setStatus] = useState<BrainIndexStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Set by the Cancel button; checked between pages (a page already running finishes).
  const cancelled = useRef(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setStatus(await fetchBrainIndexStatus(tenant));
    } catch (err) {
      setStatus(null);
      setError(err instanceof Error ? err.message : 'No se pudo consultar el índice');
    }
  }, [tenant]);

  useEffect(() => {
    void load();
  }, [load]);

  const reindex = async (): Promise<void> => {
    let offset = 0;
    const failures: string[] = [];
    let orphansRemoved = 0;
    cancelled.current = false;
    setProgress({ done: 0, total: 0 });
    try {
      for (;;) {
        // A recovery: force, or documents whose hash is intact (changed embedding model, wiped graph) would be skipped.
        const page = await reindexBrain(offset, true);
        failures.push(...page.errors);
        orphansRemoved += page.orphansRemoved ?? 0;
        setProgress({ done: page.nextOffset, total: page.total });
        if (page.done) break;
        if (cancelled.current) {
          message.info('Reindexado cancelado: puedes volver a lanzarlo, es idempotente');
          return;
        }
        offset = page.nextOffset;
      }
      if (failures.length > 0) message.warning(`Reindexado con ${failures.length} error(es)`);
      else if (orphansRemoved > 0) message.success(`Brain reindexado; ${orphansRemoved} documento(s) borrado(s) quitados del índice`);
      else message.success('Brain reindexado');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error reindexando el Brain');
    } finally {
      setProgress(null);
      await load();
    }
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title="Índice del Brain"
      extra={<Button size="small" icon={<ReloadOutlined />} onClick={() => void load()}>Actualizar</Button>}
    >
      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}
      {status && (
        <Descriptions column={3} size="small">
          <Descriptions.Item label="Documentos">{status.documents}</Descriptions.Item>
          <Descriptions.Item label="Secciones">{status.chunks}</Descriptions.Item>
          <Descriptions.Item label="Última indexación">
            {status.last_indexed_at ? new Date(status.last_indexed_at).toLocaleString('es-ES') : 'Nunca'}
          </Descriptions.Item>
        </Descriptions>
      )}
      <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
        <Text type="secondary">
          Cada documento se indexa al guardarlo. Si el índice se ha quedado atrás (cambio de modelo de
          embeddings, índice vaciado o un fallo del indexador), reindexa todos los documentos.
        </Text>
        {progress ? (
          <Space style={{ width: '100%' }}>
            <Progress
              style={{ minWidth: 240 }}
              percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
              size="small"
            />
            <Button size="small" onClick={() => { cancelled.current = true; }}>Cancelar</Button>
          </Space>
        ) : (
          <Button icon={<SyncOutlined />} onClick={() => void reindex()}>Reindexar todo</Button>
        )}
      </Space>
    </Card>
  );
};

export default BrainIndexCard;
